// @mui
import { Grid, Typography } from '@mui/material';
// hooks
// components
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'src/redux/store';
import { loadingIntegrationsState } from 'src/redux/slices/integrationSlice';
import { useState } from 'react';
import { updateDoc, doc } from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import { getCollection } from 'src/api';
import { Collection } from 'src/enum';
import { toast } from 'sonner';
import { selectCurrentWorkspaceId } from 'src/redux/slices/userSlice';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useNavigate } from 'react-router-dom';
import { WidgetType } from 'src/enum';
import SkeletonMap from 'src/components/skeleton/SkeletonMap';
import { range } from 'lodash';
import { useTranslation } from 'react-i18next';
import { selectCurrentPlatformIntegrations } from 'src/redux/selectors/integrations';
import { Button } from 'src/components/ui/button';
import { widgetsConfig } from 'src/pages/Integrations/config';
import WidgetCardItem from 'src/components/WidgetCardItem';
import { DrawerTrigger } from 'src/components/ui/drawer';
import WidgetTitleEditDialog from 'src/components/WidgetTitleEditDialog';
// ----------------------------------------------------------------------

function LoadingGrid({ children }: any) {
  const isLoading = useSelector(loadingIntegrationsState);
  if (isLoading) {
    return (
      <>
        {range(0, 9).map((i: number) => (
          <Grid key={i} item xs={12} md={4}>
            <SkeletonMap height={280} />
          </Grid>
        ))}
      </>
    );
  }
  return children;
}

export function getIntegration(type: WidgetType) {
  switch (type) {
    case WidgetType.MICRO_STARS:
      return {
        title: widgetsConfig.minireviewcount.title,
        cover: widgetsConfig.minireviewcount.cover,
      };
    case WidgetType.COMPACT_STARS:
      return {
        title: widgetsConfig.compactreviewcount.title,
        cover: widgetsConfig.compactreviewcount.cover,
      };
    case WidgetType.HEADER_BAR_STARS:
      return {
        title: widgetsConfig.headerbar.title,
        cover: widgetsConfig.headerbar.cover,
      };
    case WidgetType.REVIEW_CARDS:
      return {
        title: widgetsConfig.textcarousel.title,
        cover: widgetsConfig.textcarousel.cover,
      };
    // case WidgetType.BEFORE_AFTER_CARDS:
    //   return { title: 'Før/Efter anmeldelser', cover: beforeAfterWidget };
    case WidgetType.VIDEO_CARDS:
      return {
        title: widgetsConfig.videocarousel.title,
        cover: widgetsConfig.videocarousel.cover,
      };
    case WidgetType.TEXT_VIDEO_CARDS:
      return {
        title: widgetsConfig.textvideocarousel.title,
        cover: widgetsConfig.textvideocarousel.cover,
      };
    case WidgetType.TRANSFORMATION_IMAGES:
      return {
        title: widgetsConfig.transformationimages.title,
        cover: widgetsConfig.transformationimages.cover,
      };

    default:
      console.warn('Non supported widget');
      return { title: '', cover: '' };
  }
}

export default function Widgets() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const integrations = useSelector(selectCurrentPlatformIntegrations);
  const workspaceId = useSelector(selectCurrentWorkspaceId);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<{
    id: string;
    currentTitle: string;
    customTitle?: string;
  } | null>(null);

  const handleEditClick = (widgetId: string, currentTitle: string, customTitle?: string) => {
    setEditingWidget({
      id: widgetId,
      currentTitle,
      customTitle,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingWidget(null);
  };

  return (
    <Grid container spacing={2}>
      <LoadingGrid>
        {integrations?.length < 1 && (
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <h3 className="text-3xl font-bold text-gray-400 mt-8 mb-4">
              {t('widgets.empty.headline')}
            </h3>
            <p className="text-gray-400 mb-4">{t('widgets.empty.description')}</p>
            <DrawerTrigger asChild>
              <Button>{t('widgets.empty.cta.btn')}</Button>
            </DrawerTrigger>
          </Grid>
        )}

        {integrations.map((i: any) => {
          const { title, cover } = getIntegration(i.type);
          return (
            <Grid key={i.id} item xs={3}>
              <WidgetCardItem
                widgetId={i.id}
                title={t(title)}
                customTitle={i.title}
                cover={cover}
                state={i.state}
                onClick={() => navigate(`${PATH_DASHBOARD.general.integrations}/${i.id}`)}
                onEditClick={handleEditClick}
              />
            </Grid>
          );
        })}
      </LoadingGrid>

      {editingWidget && (
        <WidgetTitleEditDialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          widgetId={editingWidget.id}
          workspaceId={workspaceId!}
          title={editingWidget.customTitle || editingWidget.currentTitle}
        />
      )}
    </Grid>
  );
}
